import { useCallback, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { DEFAULT_LOCALE, LANG_STORAGE_KEY, LOCALES, LOCALE_IDS, isLocaleId, translate } from './index';
import type { LocaleId } from './index';
import { I18nContext } from './context';
import type { I18nValue } from './context';

function readStoredLocale(): LocaleId {
    try {
        const saved = localStorage.getItem(LANG_STORAGE_KEY);
        if (isLocaleId(saved)) return saved;
    } catch {
        // מצב פרטי חוסם localStorage — נופלים לברירת המחדל.
    }
    return DEFAULT_LOCALE;
}

function applyLocaleToDocument(locale: LocaleId): void {
    const root = document.documentElement;
    root.lang = locale;
    root.dir = LOCALES[locale].dir;
}

export function I18nProvider({ children }: { children: ReactNode }) {
    const [locale, setLocaleState] = useState<LocaleId>(readStoredLocale);

    const setLocale = useCallback((next: LocaleId) => {
        setLocaleState(next);
        applyLocaleToDocument(next);
        try {
            localStorage.setItem(LANG_STORAGE_KEY, next);
        } catch {
            // אין מה לעשות — השפה פשוט לא תישמר לפעם הבאה.
        }
    }, []);

    const value = useMemo<I18nValue>(
        () => ({
            locale,
            localeDef: LOCALES[locale],
            t: (key, params) => translate(locale, key, params),
            setLocale,
            cycleLocale: () => {
                const i = LOCALE_IDS.indexOf(locale);
                const next = LOCALE_IDS[(i + 1) % LOCALE_IDS.length];
                if (next) setLocale(next);
            },
        }),
        [locale, setLocale],
    );

    return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}
